import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import queryString from 'query-string';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useFetch from '../helpers/useFetch';
import { addUserAction } from '../store/authSlice';

/**
 * This component triggers the welcome guide tour when rendered on the analytics page
 * with ?welcomeGuide=1 in the URL. It should be rendered inside the DashboardPage
 * so it mounts/unmounts naturally with page navigation.
 */
const WelcomeGuideTrigger = () => {
  const location = useLocation();
  const fetch = useFetch();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { domain } = useSelector((state) => state.domain);

  const showWelcomeGuide = queryString.parse(location.search).welcomeGuide === '1';

  const driverRef = useRef(null);
  const tourStartedRef = useRef(false);

  // Determine if we should show the welcome guide
  const welcomeCompleted = user?.userActions?.find(
    (a) => a.action === 'taskCompleted' && a.context.task === 'welcomeGuide'
  );
  const shouldAcceptTerms =
    user && domain && !user.email.endsWith('@releva.ai') && (!domain.termsAcceptedAt || !domain.pricingAcceptedAt);
  const shouldShowTour = !shouldAcceptTerms && domain && user && (!welcomeCompleted || showWelcomeGuide);

  useEffect(() => {
    // Build tour steps based on domain configuration
    const buildTourSteps = () => {
      const steps = [
        {
          element: '[data-tour="mainLogo"]',
          popover: {
            title: 'Welcome to Releva!',
            description: 'Let us give you a very brief tour of the platform.',
          },
        },
      ];

      // Add onboarding session step if not scheduled
      if (!domain?.onboardingSessionScheduledAt) {
        steps.push({
          element: '[data-tour="bookOnboardingSession"]',
          popover: {
            title: 'Book Onboarding Session',
            description:
              'Before you do anything else, we highly recommend you book an onboarding session with us. This session will help you set everything up according to our best practices.',
          },
        });
      }

      steps.push(
        {
          element: '[data-tour="analyticsLink"]',
          popover: {
            title: 'Analytics',
            description:
              'In the Analytics section, you have dashboards that show important stats for your shop. They also show what Releva does for the growth of your business. You can also download detailed reports.',
          },
        },
        {
          element: '[data-tour="webPersonalizationLink"]',
          popover: {
            title: 'Web Personalization',
            description:
              "Configure product recommendations personalized to each visitor's interests. You can choose between multiple algorithms and add fine-grained boosts and filters. You can also use banners for discount coupons or sign-up forms.",
          },
        },
        {
          element: '[data-tour="campaignLink"]',
          popover: {
            title: 'Campaign Automation',
            description:
              'Configure email campaigns and send them to your subscribers. Send one-time newsletters, continuous campaigns that recover abandoned carts, or triggered campaigns that respond to specific user actions.',
          },
        },
        {
          element: '[data-tour="workFlowsLink"]',
          popover: {
            title: 'Workflow Automation',
            description:
              'Orchestrate complex user journeys combining multiple communication channels - email, text messages, viber, push notifications - along with conditions and wait steps.',
          },
        },
        {
          element: '[data-tour="marketingChannelsLink"]',
          popover: {
            title: 'Marketing Channels',
            description:
              'Set up various messages such as emails, text messages, push notifications, and viber messages. These can then be used in scenarios you define in Workflow automation.',
          },
        },
        {
          element: '[data-tour="dataPlatformLink"]',
          popover: {
            title: 'Data Platform',
            description:
              'Explore your product catalog, user profiles and events. Create dynamic segments based on the information Releva compiled about your users and products.',
          },
        },
        {
          element: '[data-tour="assetsLink"]',
          popover: {
            title: 'Media Assets',
            description:
              'View and upload images for use in your messages. Customize product block layouts to match your own branding and design.',
          },
        },
        {
          element: '[data-tour="settingsLink"]',
          popover: {
            title: 'Settings',
            description:
              'Control various settings related to your shop. Adjust control group size, email settings, Viber, text messages, and much more.',
          },
        },
        {
          element: '[data-tour="accountDropdown"]',
          popover: {
            title: 'Shop Switcher',
            description:
              'If you have access to multiple shops in Releva, you can switch the current shop from this dropdown.',
          },
        },
        {
          element: '[data-tour="userDropdown"]',
          popover: {
            title: 'User Settings',
            description: 'From this dropdown you can control your user settings such as email address and password.',
          },
        },
        {
          element: '[data-tour="helpDropdown"]',
          popover: {
            title: 'Need help?',
            description:
              'Last but certainly not least, the help dropdown lets you access the Releva bot which can answer many of your questions, as well as links to the How-To Guide and Documentation. These resources are at your disposal to help you make the most out of Releva.',
          },
        },
        {
          element: '[data-tour="mainLogo"]',
          popover: {
            title: "That's it!",
            description: 'You are now ready to start using Releva. Enjoy!',
          },
        }
      );

      return steps;
    };

    if (shouldShowTour && !tourStartedRef.current) {
      tourStartedRef.current = true;

      // Small delay to ensure DOM elements are ready
      const timer = setTimeout(() => {
        const driverObj = driver({
          showProgress: true,
          steps: buildTourSteps(),
          nextBtnText: 'Next',
          prevBtnText: 'Previous',
          doneBtnText: 'Done',
          allowClose: true,
          popoverClass: 'releva-tour-popover',
          onDestroyed: () => {
            // Mark welcome guide as completed
            const completedTask = { action: 'taskCompleted', context: { task: 'welcomeGuide' } };
            fetch('/api/admin/userActions', {
              method: 'POST',
              body: JSON.stringify(completedTask),
            }).then(() => {
              dispatch(addUserAction(completedTask));
            });
          },
        });

        driverRef.current = driverObj;
        driverObj.drive();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [shouldShowTour, domain, fetch, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  }, []);

  return null; // This component doesn't render anything
};

export default WelcomeGuideTrigger;
